import { db } from '../../../../firebase/config';
import {
  collection, doc, addDoc, updateDoc, getDoc, getDocs, setDoc,
  query, where, orderBy, limit, serverTimestamp, onSnapshot, deleteDoc,
} from 'firebase/firestore';

const APPS     = 'applab_apps';
const VERSIONS = 'mockup_versions';
const DRAFTS   = 'mockup_drafts';
const BRANDING = 'customer_branding';

// ── Drafts ────────────────────────────────────────────────────────────────────
export async function saveDraft(uid, draft) {
  const ref = doc(db, DRAFTS, uid);
  await setDoc(ref, {
    ...draft,
    last_saved_at: serverTimestamp(),
    expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
  }, { merge: true });
  return uid;
}

export async function loadDraft(uid) {
  const snap = await getDoc(doc(db, DRAFTS, uid));
  if (!snap.exists()) return null;
  const d = snap.data();
  if (d.expires_at?.toDate?.() < new Date()) {
    await deleteDoc(snap.ref);
    return null;
  }
  return d;
}

export async function clearDraft(uid) {
  await deleteDoc(doc(db, DRAFTS, uid));
}

// ── App document ──────────────────────────────────────────────────────────────
export async function createMockupApp({ uid, displayName, name, mockup_data }) {
  const ref = await addDoc(collection(db, APPS), {
    name:            name || 'Namnlös mockup',
    tagline:         mockup_data.context.customer_name || '',
    type:            'mockup',
    status:          'generating',
    createdBy:       uid,
    ownerDisplayName: displayName || '',
    category:        'MockupN',
    emoji:           '✨',
    mockup_data,
    version:         1,
    uppdrag:         [],
    createdAt:       serverTimestamp(),
    updatedAt:       serverTimestamp(),
  });
  return ref.id;
}

export async function updateMockupApp(appId, updates) {
  await updateDoc(doc(db, APPS, appId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export function listenMockupApp(appId, cb) {
  return onSnapshot(doc(db, APPS, appId), snap => {
    if (snap.exists()) cb({ id: snap.id, ...snap.data() });
  });
}

export async function getMockupApp(appId) {
  const snap = await getDoc(doc(db, APPS, appId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// ── Versions ──────────────────────────────────────────────────────────────────
export async function listVersions(appId) {
  const q = query(
    collection(db, APPS, appId, VERSIONS),
    orderBy('version_number', 'desc'),
    limit(20),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Customer branding ─────────────────────────────────────────────────────────
export async function getCustomerBranding(customerId) {
  if (!customerId) return null;
  const snap = await getDoc(doc(db, BRANDING, customerId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export function listenCustomerBranding(customerId, cb) {
  if (!customerId) return () => {};
  return onSnapshot(doc(db, BRANDING, customerId), snap => {
    cb(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

// ── List mockups ──────────────────────────────────────────────────────────────
export async function listMockups(uid) {
  const q = query(
    collection(db, APPS),
    where('type', '==', 'mockup'),
    where('createdBy', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(50),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
